/*
 * @Author: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @Date: 2026-01-09 16:15:54
 * @LastEditors: jiakaiwen jiakaiwen@huihaiyinhe.com
 * @LastEditTime: 2026-01-13 17:15:30
 */
import { PRESET_RULE_ARRAY, NAMING_EXAMPLE } from './constants'

interface FolderLevel {
  level: number
  field: string | undefined
}

interface PhotoTitleItem {
  titleId: string
  titleName: string
}

interface FolderNamingParams {
  string1: string
  string2: string
  string3: string
  number1?: number
  number2?: number
}

// 数据转换工具函数
export const sensorsFolderType = (folderRule: string): string => {
  return folderRule === 'user'
    ? 'byPeople'
    : folderRule === 'date'
      ? 'byDate'
      : 'noFolder'
}

export const sensorsNameRule = (
  namingRule: string[],
  photoTitleList: PhotoTitleItem[]
): string[] => {
  return namingRule
    .map((rule) => {
      if (rule.includes('title')) {
        const id = rule.slice('title'.length)
        const matched = (photoTitleList || []).find(
          (it) => String(it && it.titleId) === String(id)
        )
        return matched ? matched.titleName : ''
      } else {
        switch (rule) {
          case 'user':
            return '拍摄人姓名'
          case 'time':
            return '拍摄日期'
          case 'address':
            return '拍摄地点'
          case 'watermark':
            return '水印名称'
          case 'photoNumber':
            return '照片编号'
          default:
            return ''
        }
      }
    })
    .filter((item) => item !== '')
}

// 命名规则转换（发送给服务端）
export const transformNamingRuleForServer = (
  namingRule: string[]
): string[] => {
  return namingRule.map((item) => {
    if (item.includes('title')) {
      return 'title:' + item.split('title')[1]
    } else if (!PRESET_RULE_ARRAY.includes(item)) {
      return 'diy:' + item
    } else {
      return item
    }
  })
}

// 文件夹配置转换（发送给服务端）
export const transformFolderLevelsForServer = (
  folderLevels: FolderLevel[]
): (string | undefined)[] => {
  return folderLevels
    .filter((l) => l.field !== undefined)
    .map((l) => (l.field === '' ? '' : l.field))
}

// 生成示例文件名
export const generateExampleName = (
  namingRule: string[],
  photoTitleList: PhotoTitleItem[]
): string => {
  return (
    namingRule
      .map((rule) => {
        if (NAMING_EXAMPLE[rule as keyof typeof NAMING_EXAMPLE]) {
          return NAMING_EXAMPLE[rule as keyof typeof NAMING_EXAMPLE]
        } else if (!rule.includes('title')) {
          return rule
        } else {
          for (const items of photoTitleList) {
            const value = rule.split('title')[1]
            if (value === items.titleId) {
              return items.titleName === '天气' ? '晴' : items.titleName
            }
          }
        }
        return ''
      })
      .join(' ') + '.jpg'
  )
}

// 生成每一级文件夹的命名规则参数
export const generateFolderNamingParams = (
  folderLevels: FolderLevel[],
  photoTitleList: PhotoTitleItem[],
  multiTrialLeft?: number,
  renameTrialLeft?: number
): FolderNamingParams => {
  const normalizedFolderLevels = Array.isArray(folderLevels)
    ? folderLevels
    : [
        { level: 1, field: '' },
        { level: 2, field: undefined },
        { level: 3, field: undefined }
      ]

  const presetLabels = {
    user: '拍摄人姓名',
    date: '拍摄日期',
    address: '拍摄地点',
    watermark: '水印名称',
    photoNumber: '照片编号'
  }

  const result: FolderNamingParams = {
    string1: '',
    string2: '',
    string3: ''
  }
  for (let i = 0; i < 3; i++) {
    const level = normalizedFolderLevels[i]
    const field = level?.field

    let namingRule = ''
    if (field === '' || field === undefined || field === null) {
      namingRule = '默认文件夹（不归类）'
    } else if (typeof field === 'string' && field.startsWith('title:')) {
      const titleId = field.slice('title:'.length)
      const titleItem = (photoTitleList || []).find(
        (item) => String(item.titleId) === String(titleId)
      )
      namingRule = titleItem ? titleItem.titleName : '自定义标题'
    } else {
      namingRule = presetLabels[field] || field || '默认文件夹（不归类）'
    }

    result[`string${i + 1}`] = namingRule
  }

  // 添加试用次数参数
  if (multiTrialLeft !== undefined) {
    result.number1 = 10 - multiTrialLeft || 0
  }
  if (renameTrialLeft !== undefined) {
    result.number2 = 10 - renameTrialLeft || 0
  }

  return result
}

// 基础命名项（不受限制）
const BASIC_NAMING_KEYS = ['user', 'time', 'watermark']

// 获取高级命名条目的中文标签列表
export const getAdvancedNamingLabels = (
  namingRule: string[],
  photoTitleList: PhotoTitleItem[]
): string[] => {
  if (!Array.isArray(namingRule)) return []
  const labelMap: Record<string, string> = {
    address: '拍摄地点',
    photoNumber: '照片编号'
  }
  return namingRule
    .filter((rule) => !BASIC_NAMING_KEYS.includes(rule))
    .map((rule) => {
      if (labelMap[rule]) return labelMap[rule]
      if (rule.includes('title')) {
        const id = rule.slice('title'.length)
        const matched = (photoTitleList || []).find(
          (it) => String(it && it.titleId) === String(id)
        )
        return matched ? matched.titleName : ''
      }
      return rule
    })
    .filter((label) => label !== '')
}

// 过滤掉高级命名项，只保留基础项
export const filterBasicNamingRule = (namingRule: string[]): string[] => {
  if (!Array.isArray(namingRule)) return []
  return namingRule.filter((rule) => BASIC_NAMING_KEYS.includes(rule))
}

// 判断是否为高级选择
export function isAdvancedSelection({
  folderLevels,
  namingRule
}: {
  folderLevels?: FolderLevel[]
  namingRule?: string[]
}) {
  const advancedNaming = Array.isArray(namingRule)
    ? namingRule.some((item) => {
        return !(item === 'user' || item === 'time' || item === 'watermark')
      })
    : false

  let advancedFolder = false
  if (Array.isArray(folderLevels)) {
    // 二级或三级有配置则算高级
    if (folderLevels[1] && typeof folderLevels[1].field !== 'undefined')
      advancedFolder = true
    if (folderLevels[2] && typeof folderLevels[2].field !== 'undefined')
      advancedFolder = true
    // 仅一级且为拍摄地点也视为高级
    if (
      !advancedFolder &&
      folderLevels[0] &&
      folderLevels[0].field === 'address'
    )
      advancedFolder = true
  }

  return { advancedFolder, advancedNaming }
}
